import { components } from '@convergence/client';

import { Button, useTailwindBreakpoint } from '@convergence/components';
import { ArrowRight, List } from '@phosphor-icons/react';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { fetch } from '../../api/fetch';
import { useLabsQuery } from '../../api/queries/labs';
import { useTinkerBoxesQuery } from '../../api/queries/tinkerBoxes';
import { useAuthContext } from '../../components/AuthProvider';
import { Code } from '../../components/Code';
import { ConnectionIcon } from '../../components/ConnectionIcon';
import { Help } from '../../components/Help';
import { ModeToggle } from '../../components/ModeToggle';
import { NotebookOrShell } from '../../components/NotebookOrShell';
import { SidePanel } from '../../components/side-panel/SidePanel';
import { Tour, TourStep, useTour } from '../../components/Tour';
import { UsageCountdown } from '../../components/UsageCountdown';
import { useMobxSync } from '../../hooks/useMobxStoreSync';
import { useStateParams } from '../../hooks/useStateParams';
import { useErrorToast } from '../../lib/useErrorToast';
import { homeStore } from '../../stores/HomeStore';
import { HomeContextProvider } from './HomeContext';
import { useSidePanel } from './useSidePanel';

export const Home = observer(() => {
  // const [webSocketClient] = useState(
  //   new WebSocketClient({ url: API_ENDPOINT.replace('http', 'ws') })
  // );
  const { user } = useAuthContext();

  const isLabUser = user?.permissions?.includes('lab_read') ?? false;

  const { data: labsData, error } = useLabsQuery({
    enabled: isLabUser
  });

  const { values } = useStateParams<{ selectedLabId: string }>();
  const { data: tinkerBoxesData } = useTinkerBoxesQuery(
    fetch,
    isLabUser,
    homeStore.selectedLabId
  );

  useMobxSync({ selectedLabId: +values.selectedLabId }, homeStore);

  useErrorToast(error);

  // useEffect(() => {
  //     if (!homeStore.tinkerBoxConnection.noteBookData) {
  //         homeStore.tinkerBoxConnection.initNotebook();
  //     }
  // }, [homeStore.tinkerBoxConnection.noteBookData]);

  useEffect(() => {
    if (labsData?.labs) {
      console.log({ labsData });
      homeStore.setLabs(labsData.labs);
    }
  }, [labsData]);

  useEffect(() => {
    if (tinkerBoxesData?.tinkerboxes) {
      homeStore.setTinkerBoxes(
        tinkerBoxesData as components['schemas']['GetTinkerBoxesResponse']
      );
    }
  }, [tinkerBoxesData]);

  const { showSidePanel, setShowSidePanel } = useSidePanel();

  const isLarge = useTailwindBreakpoint('sm');

  return (
    <HomeContextProvider
      value={{ focus: homeStore.focus, setFocus: homeStore.setFocus }}
    >
      {!isLabUser && <WelcomeTour />}
      <div className="w-full h-full overflow-hidden flex flex-grow-0 relative text-sm tour bg-white font-sans">
        {isLarge && (
          <SidePanel
            store={homeStore}
            open={homeStore.showSidePanel}
            setOpen={homeStore.setShowSidePanel}
            tinkerBoxConnection={homeStore.tinkerBoxConnection}
          />
        )}
        <div className="w-full h-full flex flex-col flex-grow relative p-4 bg-white overflow-hidden">
          <ModeToggle
            mode={homeStore.selectedMode}
            setMode={homeStore.setMode}
          />
          <NotebookOrShell
            store={homeStore}
            // webSocketClient={webSocketClient}
          />

          {user?.permissions?.includes('credits_unlimited') ? null : (
            <div className="absolute top-4 right-10 items-center p-3 pr-1 gap-2 bg-neutral-100 z-10">
              <div className="flex justify-between gap-2 items-center">
                <div>
                  You have{' '}
                  <UsageCountdown
                    active={
                      !!tinkerBoxesData?.tinkerboxes.some(
                        ({ status }) => status === 'running'
                      )
                    }
                  />{' '}
                  remaining <br />
                  <a className="text-orange-2 underline" href="/pricing">
                    Upgrade to a premium plan
                  </a>
                </div>
                <ConnectionIcon
                  activeConnection={
                    homeStore.tinkerBoxConnection.connectionState ===
                    'connected'
                  }
                />
              </div>
            </div>
          )}
          {showSidePanel && !isLarge && (
            <>
              <SidePanel
                open={showSidePanel}
                setOpen={setShowSidePanel}
                store={homeStore}
                className="absolute top-0 left-0 z-20 lg:hidden flex"
                tinkerBoxConnection={homeStore.tinkerBoxConnection}
              />
              <div
                onClick={() => {
                  setShowSidePanel(false);
                }}
                className="opacity-0 w-screen h-screen fixed z-20 top-0 left-0 lg:hidden block"
              ></div>
            </>
          )}
        </div>

        <button
          data-tour={'launch'}
          className="absolute top-2 left-2 z-10 lg:hidden block dark:text-neutral-200"
          onClick={() => setShowSidePanel(!showSidePanel)}
        >
          <List size={20} />
        </button>
      </div>
      <div className="fixed bottom-4 right-4 z-50">
        <Help />
      </div>
      {/* <Usage /> */}
    </HomeContextProvider>
  );
});
export function GradientOutline(props: {
  x: number;
  y: number;
  width: number;
  height: number;
}) {
  const strokeWidth = 6;
  return (
    <div
      className="fixed z-50 pointer-events-none"
      style={{
        top: `${props.y}px`,
        left: `${props.x}px`,
        width: `${props.width}px`,
        height: `${props.height}px`
      }}
    >
      <div
        style={{
          clipPath: `polygon(
            0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%,
            ${strokeWidth}px ${strokeWidth}px,
            ${strokeWidth}px calc(100% - ${strokeWidth}px),
            calc(100% - ${strokeWidth}px) calc(100% - ${strokeWidth}px),
            calc(100% - ${strokeWidth}px) ${strokeWidth}px,
            ${strokeWidth}px ${strokeWidth}px
          )`
        }}
        className="w-full h-full border-2 animate-border dark:text-neutral-200 z-50"
      ></div>
    </div>
  );
}

function ConnectTourStep(props: {
  el: Element | null;
  bounds: { x: number; y: number; width: number; height: number } | null;
  next: () => void;
}) {
  const { bounds, next, el } = props;
  useEffect(() => {
    if (!el || !bounds) return;
    const fn = () => {
      next();
    };
    (el as HTMLElement).addEventListener('click', fn);

    return () => {
      (el as HTMLElement).removeEventListener('click', fn);
    };
  }, [el, next]);
  const padding = { x: 6, y: 6 };
  return (
    <>
      {bounds && (
        <GradientOutline
          x={bounds.x - padding.x}
          y={bounds.y - padding.y}
          width={bounds.width + padding.x * 2}
          height={bounds.height + padding.y * 2}
        />
      )}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 place-items-center gap-4 bg-white z-50 w-lg p-8 flex flex-col">
        <h2 className="text-5xl mb-2 text-orange-2">That's it!</h2>
        <p>
          Now click the plug icon to connect to your Tinker Box.
          <br />
          Once connected, try typing <Code>list_samples</Code> or{' '}
          <Code>list_tools</Code> into the terminal to see what’s available. You
          can also type any tool name followed by <Code>-h</Code> for parameter
          information.
        </p>
        <p>
          Want to call loops? Start{' '}
          <a
            className="text-orange-2 inline-block"
            href="https://docs.axiotl.com/workflows/snippets/calling_loops"
            target="_blank"
            rel="noopener noreferrer"
          >
            here
          </a>
        </p>

        <Button
          onClick={() => {
            next();
          }}
        >
          Finish
        </Button>
      </div>
    </>
  );
}

function WelcomeTour() {
  const { tourShown, setTourShown } = useTour();
  const padding = { x: 8, y: 8 };
  return (
    <Tour
      visible={!tourShown}
      onEnd={() => {
        setTourShown(true);
      }}
    >
      <TourStep
        name="intro-1"
        render={(_, __, next) => (
          <>
            <div className="fixed w-full h-full bg-neutral-600 opacity-50 top-0 left-0 animate-none z-40"></div>
            <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 place-items-center gap-4 bg-white z-50 w-lg p-8 flex flex-col">
              <h2 className="text-5xl mb-2 text-orange-2">
                Welcome to Tinker!
              </h2>
              <p>
                Tinker Boxes are cloud-based computers built for 3D genomics.
              </p>
              <p>Your Tinker Box is ready. Let's go over how it works.</p>
              <div className="flex gap-2">
                <button
                  className="px-2 py-1 bg-orange-2 rounded-sm text-white"
                  onClick={() => {
                    next();
                  }}
                >
                  <ArrowRight size={24} />
                </button>
              </div>
            </div>
          </>
        )}
      />
      <TourStep name="storage" padding={padding}>
        <p>
          This is your file manager.
          <br /> Upload, download, and view everything in your Tinker Box from
          here.
        </p>
      </TourStep>
      <TourStep name="tinkerboxes" padding={padding}>
        <div className="p-2">
          This is your control panel.
          <br />
          View, start, stop, and connect to your Tinker Box from here.
        </div>
      </TourStep>
      <TourStep name="status" padding={padding}>
        <p>
          This icon shows the status of your Tinker Box.
          <br />
          <span className="text-green-500">Green</span> = ready to connect
          <br />
          <span className="text-yellow-500">Yellow</span> = starting or stopping
          <br />
          <span className="text-red-500">Red</span> = off
        </p>
      </TourStep>
      <TourStep name="play-pause" padding={padding}>
        <p>
          Use the play/pause icon to start or stop your Tinker Box.
          <br />
          The status icon will turn yellow while processing your request.
        </p>
      </TourStep>
      <TourStep name="connect" padding={padding}>
        <p>
          When the status icon is green, use the terminal icon to connect to
          your Tinker Box
        </p>
      </TourStep>
      <TourStep name="name" padding={padding}>
        <p>This is the name of your Tinker Box</p>
      </TourStep>
      <TourStep name="docs" side="left" padding={padding}>
        <p>
          Need ideas, help, or want to see this tutorial again? Click here to
          open the Docs.
        </p>
      </TourStep>
      <TourStep
        name="connect"
        background="highlight"
        render={(node, bounds, next) => (
          <ConnectTourStep el={node} bounds={bounds} next={next} />
        )}
      />
    </Tour>
  );
}
